import { cloneDeep } from 'lodash';
import { BeaconWeldingData } from '../../beacon/welding/model/beacon-welding-data';
import { instanceOfFlowItemMultipassSectionParent } from '../../beacon/welding/util/beacon-welding.util';
import { FlowItem } from '../model/flow.types';
import { instanceOfFlowItem } from './flow-item.util';

export interface MultipassDetectionResult {
  updatedItems: FlowItem[];
  multipassSectionsToUpdate: BeaconWeldingData.FlowItemMultipassSectionParent[];
}

/**
 * Detects multipass sections that need to be updated when items are modified.
 * This function checks if any updated items are referenced in multipass section originalItems
 * and returns both the updated items and the affected multipass sections.
 */
export const detectAffectedMultipassSections = (
  items: FlowItem[],
  updatedItemIds: Set<string>
): MultipassDetectionResult => {
  const clonedItems = cloneDeep(items);
  const multipassSectionsToUpdate: BeaconWeldingData.FlowItemMultipassSectionParent[] =
    [];

  for (const item of clonedItems) {
    if (instanceOfFlowItemMultipassSectionParent(item)) {
      // Check if any of the updated items are in this multipass section's originalItems
      const hasUpdatedOriginalItems = item.attributes.originalItems.some(
        (originalItem) =>
          instanceOfFlowItem(originalItem) &&
          originalItem.id !== undefined &&
          updatedItemIds.has(originalItem.id)
      );

      if (hasUpdatedOriginalItems) {
        multipassSectionsToUpdate.push(item);
      }
    }
  }

  // Update originalItems in multipass sections
  for (const multipassSection of multipassSectionsToUpdate) {
    const updatedOriginalItems = multipassSection.attributes.originalItems.map(
      (originalItem) => {
        if (instanceOfFlowItem(originalItem) && originalItem.id) {
          const updatedItem = clonedItems.find(
            (item) => item.id === originalItem.id
          );
          return updatedItem || originalItem;
        }
        return originalItem;
      }
    );

    // Update the multipass section with the new originalItems
    const sectionIndex = clonedItems.findIndex(
      (item) => item.id === multipassSection.id
    );
    if (sectionIndex !== -1) {
      clonedItems[sectionIndex] = {
        ...multipassSection,
        attributes: {
          ...multipassSection.attributes,
          originalItems: updatedOriginalItems,
        },
      };
    }
  }

  return { updatedItems: clonedItems, multipassSectionsToUpdate };
};

/**
 * Creates a set of item IDs from an array of FlowItems.
 * Filters out undefined IDs.
 */
export const createUpdatedItemIds = (items: FlowItem[]): Set<string> => {
  return new Set(
    items.map((item) => item.id).filter((id): id is string => id !== undefined)
  );
};
